import axios, { AxiosResponse } from 'axios';
import {
  ValidationResult,
  PagedResponse,
  Order,
  Product,
  ServiceType,
  Make,
  Model,
  Technician,
  TechnicianPerformance,
  Lead,
  LeadFollowUp,
  LeadSummary,
  DashboardStats,
  OrderQueryRequest,
  OrderStatusUpdateRequest,
  Contest,
  ContestLeaderboardEntry,
  AdminRole,
  AdminUser,
  AdminUserRequest,
  LeadCallLog,
  TechnicianStockItem,
  TechnicianStockTransaction,
  IssueStockRequest,
  ReturnStockRequest,
  Blog,
} from '../types';

class ApiService {
   private baseURL = 'https://cij3b8xauj.execute-api.ap-south-1.amazonaws.com/Prod/api';
   //private baseURL = 'https://localhost:44323/api';

  private api = axios.create({
    baseURL: this.baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('isLoggedIn');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Products
  async getProducts(page: number = 1, pageSize: number = 10, search: string = '', makeId?: number, modelId?: number, serviceName?: string): Promise<ValidationResult<any>> {
    const response = await this.api.get('/v1/Model/products', {
      params: { page, pageSize, search, ...(makeId ? { makeId } : {}), ...(modelId ? { modelId } : {}), ...(serviceName ? { serviceName } : {}) }
    });
    return response.data;
  }

  async getProductServiceTypes(): Promise<string[]> {
    const response = await this.api.get('/v1/Model/product-service-types');
    return response.data?.entity || [];
  }

  async getProduct(id: number): Promise<ValidationResult<Product>> {
    const response = await this.api.get(`/v1/Model/products/${id}`);
    return response.data;
  }

  async getProductSpecifications(serviceId: number): Promise<{ isSuccess: boolean; entity: { specKey: string; specValue: string }[] }> {
    const response = await this.api.get(`/v1/Model/products/${serviceId}/specifications`);
    return response.data;
  }

  async saveProductSpecifications(serviceId: number, specs: { specKey: string; specValue: string }[]): Promise<{ isSuccess: boolean }> {
    const response = await this.api.post(`/v1/Model/products/${serviceId}/specifications`, specs);
    return response.data;
  }

  async updateProduct(id: number, product: FormData): Promise<ValidationResult<Product>> {
    const response = await this.api.post(`/v1/Model/products/${id}`, product, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async createProduct(product: FormData): Promise<ValidationResult<Product>> {
    const response = await this.api.post(`/v1/Model/products`, product, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteProduct(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/Model/products/${id}`);
    return response.data;
  }

  // Makes
  async getMakes(): Promise<ValidationResult<Make[]>> {
    const response = await this.api.get('/v1/Make/makes');
    return response.data;
  }

  // Models
  async getModels(makeId: number): Promise<ValidationResult<Model[]>> {
    const response = await this.api.get(`/v1/model/${makeId}/models`);
    return response.data;
  }

  // Service Types
  async getServiceTypes(modelId: number): Promise<ValidationResult<ServiceType[]>> {
    const response = await this.api.get(`/v1/model/${modelId}/servicetypes`);
    return response.data;
  }

  async updateServicePrice(serviceId: number, actualPrice: number, marginalPrice: number): Promise<ValidationResult<boolean>> {
    const response = await this.api.patch(`/v1/model/products/${serviceId}/price`, { actualPrice, marginalPrice });
    return response.data;
  }

  async updateOrderItemPrice(orderId: number, serviceId: number, actualPrice: number): Promise<ValidationResult<boolean>> {
    const response = await this.api.patch(`/v1/order/${orderId}/items/${serviceId}/price`, { actualPrice });
    return response.data;
  }

  // Orders - matching Angular API exactly
  async searchOrders(
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = '',
    startDate?: Date,
    endDate?: Date,
    status?: string,
    technicianId?: number
  ): Promise<ValidationResult<PagedResponse<Order>>> {
    const params: any = {
      page: page.toString(),
      pageSize: pageSize.toString()
    };

    if (searchTerm) params.searchTerm = searchTerm;
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (status) params.status = status;
    if (technicianId) params.technicianId = technicianId;

    const response = await this.api.get('/v1/Order/search', { params });
    return response.data;
  }

  async getOrders(queryRequest: OrderQueryRequest): Promise<ValidationResult<PagedResponse<Order>>> {
    const params: any = {
      page: queryRequest.page,
      pageSize: queryRequest.pageSize
    };
    
    if (queryRequest.search) params.searchTerm = queryRequest.search;
    if (queryRequest.status) params.status = queryRequest.status;
    if (queryRequest.startDate) params.startDate = queryRequest.startDate;
    if (queryRequest.endDate) params.endDate = queryRequest.endDate;

    const response = await this.api.get('/v1/Order/search', { params });
    return response.data;
  }

  async getOrder(id: number): Promise<ValidationResult<Order>> {
    const response = await this.api.get(`/v1/Order/${id}`);
    return response.data;
  }

  async createOrder(order: Order): Promise<ValidationResult<Order>> {
    const response = await this.api.post('/v1/Order', order);
    return response.data;
  }

  async updateOrder(id: number, order: Order): Promise<ValidationResult<Order>> {
    const response = await this.api.put(`/v1/Order/${id}`, order);
    return response.data;
  }

  async deleteOrder(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/Order/${id}`);
    return response.data;
  }

  async updateOrderStatus(orderId: number, request: OrderStatusUpdateRequest): Promise<ValidationResult<void>> {
    const response = await this.api.put(`/v1/Order/${orderId}/status`, request);
    return response.data;
  }

  async getPaymentStatus(orderId: number): Promise<string> {
    const response = await this.api.get(`/v1/payment/status/${orderId}`);
    if (typeof response.data === 'string') return response.data;
    return response.data?.status || response.data?.paymentStatus || response.data?.orderStatus || '';
  }

  async assignTechnicianToOrder(orderId: number, technicianId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Technician/orders/${orderId}/assign/${technicianId}`, {});
    return response.data;
  }

  async getOrdersByCustomer(mobile: string): Promise<ValidationResult<Order[]>> {
    const response = await this.api.get(`/v1/Order/customer/${mobile}`);
    return response.data;
  }

  async addOrderPayment(id: number, payment: { orderId: number; paymentMethod: string; paymentAmount: number; transactionId?: string }): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Order/${id}/payment`, payment);
    return response.data;
  }

  async getOrderCount(): Promise<ValidationResult<number>> {
    const response = await this.api.get('/v1/Order/count');
    return response.data;
  }

  async sendOrderDetails(orderId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Order/${orderId}/SendOrderDetails`, {});
    return response.data;
  }

  async sendBill(orderId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Order/${orderId}/SendBill`, {});
    return response.data;
  }

  async sendPaymentQr(orderId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/Order/${orderId}/send-payment-qr`, {});
    return response.data;
  }

  async callOrderConfirmation(orderId: number): Promise<{ message: string; callSid?: string }> {
    const response = await this.api.post(`/v1/call/order-confirmation/${orderId}`, {});
    return response.data;
  }

  async exotelCallOrder(orderId: number, agentNumber: string): Promise<ValidationResult<any>> {
    const response = await this.api.post(`/v1/call/order/${orderId}/exotel`, { agentNumber });
    return response.data;
  }

  async getLeadPipelineStats(): Promise<any> {
    const response = await this.api.get('/v1/Leads/pipeline-stats');
    return response.data;
  }

  async sendDailyFollowUps(): Promise<any> {
    const response = await this.api.post('/v1/Leads/send-follow-ups', {});
    return response.data;
  }

  async getCtaStats(): Promise<any> {
    const response = await this.api.get('/v1/webhook/cta-stats');
    return response.data;
  }

  // Dashboard
  async getDashboardStats(startDate?: string, endDate?: string, technicianId?: number | null, orderType?: string | null, orderStatus?: string | null): Promise<ValidationResult<DashboardStats>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (technicianId) params.technicianId = technicianId.toString();
    if (orderType) params.orderType = orderType;
    if (orderStatus) params.orderStatus = orderStatus;

    const response = await this.api.get('/v1/dashboard/stats', { params });
    return response.data;
  }

  // Technicians
  async getTechnicians(): Promise<ValidationResult<Technician[]>> {
    const response = await this.api.get('/v1/Technician');
    return response.data;
  }

  async getTechnician(id: number): Promise<ValidationResult<Technician>> {
    const response = await this.api.get(`/v1/Technician/${id}`);
    return response.data;
  }

  async createTechnician(technician: FormData): Promise<ValidationResult<Technician>> {
    const response = await this.api.post('/v1/Technician', technician, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateTechnician(id: number, technician: FormData): Promise<ValidationResult<Technician>> {
    const response = await this.api.put(`/v1/Technician/${id}`, technician, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteTechnician(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/technician/${id}`);
    return response.data;
  }

  async assignTechnicianDarkStore(technicianId: number, darkStoreId: number | null): Promise<ValidationResult<void>> {
    const response = await this.api.patch(`/v1/Technician/${technicianId}/dark-store`, darkStoreId);
    return response.data;
  }

  // Technician Stock
  async getTechnicianStock(technicianId?: number): Promise<TechnicianStockItem[]> {
    const params = technicianId ? { technicianId } : {};
    const response = await this.api.get('/v1/technician-stock', { params });
    return response.data;
  }

  async getTechnicianStockTransactions(technicianId?: number, orderId?: number, pageSize: number = 100): Promise<TechnicianStockTransaction[]> {
    const params: any = { pageSize };
    if (technicianId) params.technicianId = technicianId;
    if (orderId) params.orderId = orderId;
    const response = await this.api.get('/v1/technician-stock/transactions', { params });
    return response.data;
  }

  async issueTechnicianStock(request: IssueStockRequest): Promise<{ transaction: TechnicianStockTransaction; whatsappWarning?: string }> {
    const response = await this.api.post('/v1/technician-stock/issue', request);
    return response.data;
  }

  /** Smart issue: uses technician's own stock first, falls back to dark-store inventory. */
  async issueSmartTechnicianStock(request: IssueStockRequest): Promise<{ transaction: TechnicianStockTransaction; usedTechStock: boolean; whatsappWarning?: string }> {
    const response = await this.api.post('/v1/technician-stock/issue-smart', request);
    return response.data;
  }

  /** Returns how many units of a service the technician currently holds (0 if none). */
  async getTechStockQty(technicianId: number, serviceId: number): Promise<number> {
    const items: TechnicianStockItem[] = await this.getTechnicianStock(technicianId);
    const match = items.find(i => i.serviceId === serviceId);
    return match?.quantity ?? 0;
  }

  async returnTechnicianStock(request: ReturnStockRequest): Promise<TechnicianStockTransaction> {
    const response = await this.api.post('/v1/technician-stock/return', request);
    return response.data;
  }

  async getTechnicianPerformance(id: number): Promise<ValidationResult<TechnicianPerformance>> {
    const response = await this.api.get(`/v1/technician/${id}/performance`);
    return response.data;
  }

  async getTechnicianWorkload(technicianId: number): Promise<ValidationResult<Order[]>> {
    const response = await this.api.get(`/v1/technician/${technicianId}/workload`);
    return response.data;
  }

  async uploadTechnicianPhoto(file: File): Promise<ValidationResult<string>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/v1/Technician/upload-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Admin Users
  async getAdminUsers(): Promise<ValidationResult<AdminUser[]>> {
    const response = await this.api.get('/v1/admin-users');
    return response.data;
  }

  async getAdminRoles(): Promise<ValidationResult<AdminRole[]>> {
    const response = await this.api.get('/v1/admin-users/roles');
    return response.data;
  }

  async createAdminUser(user: AdminUserRequest): Promise<ValidationResult<AdminUser>> {
    const response = await this.api.post('/v1/admin-users', user);
    return response.data;
  }

  async updateAdminUser(id: number, user: AdminUserRequest): Promise<ValidationResult<AdminUser>> {
    const response = await this.api.put(`/v1/admin-users/${id}`, user);
    return response.data;
  }

  async setAdminUserStatus(id: number, isActive: boolean): Promise<ValidationResult<void>> {
    const response = await this.api.put(`/v1/admin-users/${id}/status`, { isActive });
    return response.data;
  }

  async sendAdminOtp(mobile: string): Promise<ValidationResult<void>> {
    const response = await this.api.post('/v1/admin-auth/send-otp', { mobile });
    return response.data;
  }

  async verifyAdminOtp(mobile: string, otp: string): Promise<{ token: string; expiresAt: string; user: AdminUser }> {
    const response = await this.api.post('/v1/admin-auth/verify-otp', { mobile, otp });
    return response.data;
  }

  // Leads
  async getLeads(page: number = 1, pageSize: number = 10, search: string = '', status?: string, startDate?: string, endDate?: string): Promise<ValidationResult<PagedResponse<Lead>>> {
    const params: any = { page, pageSize, search };
    if (status) params.status = status;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await this.api.get('/v1/leads', { params });
    return response.data;
  }

  async searchLeads(
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = '',
    startDate?: Date,
    endDate?: Date,
    status?: string,
    source?: string,
    priority?: string,
    followUpDate?: string
  ): Promise<ValidationResult<PagedResponse<Lead>>> {
    const params: any = { page, pageSize };
    if (searchTerm) params.search = searchTerm;
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    if (status) params.status = status;
    if (source) params.source = source;
    if (priority) params.priority = priority;
    if (followUpDate) params.followUpDate = followUpDate;

    const response = await this.api.get('/v1/leads/search', { params });
    return response.data;
  }

  async getLead(id: number): Promise<ValidationResult<Lead>> {
    const response = await this.api.get(`/v1/leads/${id}`);
    return response.data;
  }

  async createLead(lead: Lead): Promise<ValidationResult<Lead>> {
    const response = await this.api.post('/v1/leads', lead);
    return response.data;
  }

  async updateLead(id: number, lead: Lead): Promise<ValidationResult<Lead>> {
    const response = await this.api.put(`/v1/leads/${id}`, lead);
    return response.data;
  }

  async deleteLead(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/leads/${id}`);
    return response.data;
  }

  async updateLeadStatus(id: number, status: string): Promise<ValidationResult<void>> {
    const response = await this.api.put(`/v1/leads/${id}/status`, { status });
    return response.data;
  }

  async sendQuotation(id: number, request?: any): Promise<ValidationResult<any>> {
    const response = await this.api.post(`/v1/leads/${id}/send-quotation`, request || {});
    return response.data;
  }

  async assignLead(id: number, technicianId: number): Promise<ValidationResult<void>> {
    const response = await this.api.post(`/v1/leads/${id}/assign`, { technicianId });
    return response.data;
  }

  async getLeadSummary(startDate?: Date, endDate?: Date): Promise<ValidationResult<LeadSummary> | LeadSummary> {
    const params: any = {};
    if (startDate) {
      // Format as YYYY-MM-DD to avoid timezone conversion issues
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      const day = String(startDate.getDate()).padStart(2, '0');
      params.startDate = `${year}-${month}-${day}`;
    }
    if (endDate) {
      // Format as YYYY-MM-DD to avoid timezone conversion issues
      const year = endDate.getFullYear();
      const month = String(endDate.getMonth() + 1).padStart(2, '0');
      const day = String(endDate.getDate()).padStart(2, '0');
      params.endDate = `${year}-${month}-${day}`;
    }
    
    const response = await this.api.get('/v1/leads/summary', { params });
    // API returns data directly, not wrapped in ValidationResult
    return response.data;
  }

  // Lead Follow-ups
  async getLeadFollowUps(leadId: number): Promise<ValidationResult<LeadFollowUp[]>> {
    const response = await this.api.get(`/v1/leads/${leadId}/followups`);
    return response.data;
  }

  async getLeadCallLogs(leadId: number): Promise<ValidationResult<LeadCallLog[]>> {
    const response = await this.api.get(`/v1/leads/${leadId}/calls`);
    return response.data;
  }

  async getLeadCallLogsByPhone(mobile: string): Promise<ValidationResult<LeadCallLog[]>> {
    const response = await this.api.get(`/v1/leads/calls/by-phone/${mobile}`);
    return response.data;
  }

  async exotelCallLead(leadId: number, agentNumber: string): Promise<ValidationResult<LeadCallLog>> {
    const response = await this.api.post(`/v1/leads/${leadId}/calls/exotel`, { agentNumber });
    return response.data;
  }

  async getLeadCallRecordingBlob(leadId: number, callLogId: number): Promise<Blob> {
    const response = await this.api.get(`/v1/leads/${leadId}/calls/${callLogId}/recording`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async createLeadFollowUp(leadId: number, followUp: LeadFollowUp): Promise<ValidationResult<LeadFollowUp>> {
    const response = await this.api.post(`/v1/leads/followups`, followUp);
    return response.data;
  }

  async updateLeadFollowUp(leadId: number, followUpId: number, followUp: LeadFollowUp): Promise<ValidationResult<LeadFollowUp>> {
    const response = await this.api.put(`/v1/leads/followups/${followUpId}`, followUp);
    return response.data;
  }

  // Content Creator
  async generateContent(prompt: string): Promise<ValidationResult<{ content: string }>> {
    const params = new URLSearchParams();
    params.set('content', prompt);
    const response = await this.api.get(`/contentwriter/create?${params}`);
    return response.data;
  }

  // Order Feedback
  async getOrderFeedback(orderId: number): Promise<ValidationResult<any>> {
    const response = await this.api.get(`/v1/OrderFeedback/${orderId}`);
    return response.data;
  }

  async createOrderFeedback(formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.post('/v1/OrderFeedback', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async updateOrderFeedback(id: number, formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.put(`/v1/OrderFeedback/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async createOrderFeedbackWithPhoto(formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.post('/v1/OrderFeedback/with-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Customers
  async getCustomers(page: number = 1, pageSize: number = 10, search: string = ''): Promise<ValidationResult<any>> {
    const response = await this.api.get('/v1/customers', {
      params: { page, pageSize, search }
    });
    return response.data;
  }

  async getCustomer(id: number): Promise<ValidationResult<any>> {
    const response = await this.api.get(`/v1/customers/${id}`);
    return response.data;
  }

  // Utility methods
  getFullImageUrl(imagePath: string): string {
    const baseImageUrl = 'https://mintorepairs.com/uploads/assets/';
    return `${baseImageUrl}${imagePath}`;
  }

  // ── Expenses ──────────────────────────────────────────────────────────────

  async getExpenses(params: {
    page?: number; pageSize?: number; expenseType?: string;
    startDate?: string; endDate?: string; search?: string;
  } = {}): Promise<ValidationResult<any>> {
    const response = await this.api.get('/v1/expense', { params });
    return response.data;
  }

  async getExpense(id: number): Promise<ValidationResult<any>> {
    const response = await this.api.get(`/v1/expense/${id}`);
    return response.data;
  }

  async createExpense(formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.post('/v1/expense', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async updateExpense(id: number, formData: FormData): Promise<ValidationResult<any>> {
    const response = await this.api.put(`/v1/expense/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteExpense(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/expense/${id}`);
    return response.data;
  }

  // ── Contests ──────────────────────────────────────────────────────────────

  async getContests(): Promise<ValidationResult<Contest[]>> {
    const response = await this.api.get('/v1/contest');
    return response.data;
  }

  async getContestById(id: number): Promise<ValidationResult<Contest>> {
    const response = await this.api.get(`/v1/contest/${id}`);
    return response.data;
  }

  async createContest(contest: Partial<Contest>): Promise<ValidationResult<Contest>> {
    const response = await this.api.post('/v1/contest', contest);
    return response.data;
  }

  async updateContest(id: number, contest: Partial<Contest>): Promise<ValidationResult<Contest>> {
    const response = await this.api.put(`/v1/contest/${id}`, contest);
    return response.data;
  }

  async deleteContest(id: number): Promise<ValidationResult<any>> {
    const response = await this.api.delete(`/v1/contest/${id}`);
    return response.data;
  }

  async getContestLeaderboard(id: number): Promise<ValidationResult<ContestLeaderboardEntry[]>> {
    const response = await this.api.get(`/v1/contest/${id}/leaderboard`);
    return response.data;
  }

  async markContestRewardPaid(contestId: number, technicianId: number): Promise<ValidationResult<any>> {
    const response = await this.api.post(`/v1/contest/${contestId}/reward-paid`, null, {
      params: { technicianId },
    });
    return response.data;
  }

  // ── Order Schedules ──────────────────────────────────────────────
  async getSchedulesByOrder(orderId: number): Promise<ValidationResult<import('../types').OrderSchedule[]>> {
    const response = await this.api.get(`/v1/OrderSchedule/order/${orderId}`);
    return response.data;
  }

  async createSchedule(schedule: import('../types').OrderSchedule): Promise<ValidationResult<import('../types').OrderSchedule>> {
    const response = await this.api.post('/v1/OrderSchedule', schedule);
    return response.data;
  }

  async updateSchedule(id: number, schedule: import('../types').OrderSchedule): Promise<ValidationResult<import('../types').OrderSchedule>> {
    const response = await this.api.put(`/v1/OrderSchedule/${id}`, schedule);
    return response.data;
  }

  async deleteSchedule(id: number): Promise<ValidationResult<void>> {
    const response = await this.api.delete(`/v1/OrderSchedule/${id}`);
    return response.data;
  }

  // ── Order Replacements ──────────────────────────────────────────
  async getAllReplacements(status?: string, search?: string) {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await this.api.get('/v1/OrderReplacement', { params });
    return response.data;
  }

  async getReplacementsByOrder(orderId: number) {
    const response = await this.api.get(`/v1/OrderReplacement/order/${orderId}`);
    return response.data;
  }

  async createReplacement(replacement: import('../types').OrderReplacement) {
    const response = await this.api.post('/v1/OrderReplacement', replacement);
    return response.data;
  }

  async updateReplacement(id: number, replacement: import('../types').OrderReplacement) {
    const response = await this.api.put(`/v1/OrderReplacement/${id}`, replacement);
    return response.data;
  }

  async deleteReplacement(id: number) {
    const response = await this.api.delete(`/v1/OrderReplacement/${id}`);
    return response.data;
  }

  async sendReplacementMessage(id: number) {
    const response = await this.api.post(`/v1/OrderReplacement/${id}/send-message`);
    return response.data;
  }

  // ── Inventory / Stock Management ──────────────────────────────────────────

  // Dark Stores
  getDarkStores(): Promise<any> { return this.api.get('/v1/inventory/dark-stores').then(r => r.data); }
  createDarkStore(data: any): Promise<any> { return this.api.post('/v1/inventory/dark-stores', data).then(r => r.data); }
  updateDarkStore(id: number, data: any): Promise<any> { return this.api.put(`/v1/inventory/dark-stores/${id}`, data).then(r => r.data); }
  deleteDarkStore(id: number): Promise<any> { return this.api.delete(`/v1/inventory/dark-stores/${id}`).then(r => r.data); }

  // Inventory
  getInventory(params?: any): Promise<any> { return this.api.get('/v1/inventory', { params }).then(r => r.data); }
  getLowStockItems(): Promise<any> { return this.api.get('/v1/inventory/low-stock').then(r => r.data); }
  getInventoryTransactions(params?: any): Promise<any> { return this.api.get('/v1/inventory/transactions', { params }).then(r => r.data); }
  updateServiceStatus(serviceId: number, status: number): Promise<any> { return this.api.patch(`/v1/Model/products/${serviceId}/status`, status, { headers: { 'Content-Type': 'application/json' } }).then(r => r.data); }
  getInventorySummary(darkStoreId: number, makeId?: number, modelId?: number): Promise<any> { return this.api.get('/v1/inventory/summary', { params: { darkStoreId, ...(makeId ? { makeId } : {}), ...(modelId ? { modelId } : {}) } }).then(r => r.data); }
  restockInventory(data: any): Promise<any> { return this.api.post('/v1/inventory/restock', data).then(r => r.data); }
  adjustInventory(data: any): Promise<any> { return this.api.post('/v1/inventory/adjust', data).then(r => r.data); }
  transferInventory(data: any): Promise<any> { return this.api.post('/v1/inventory/transfer', data).then(r => r.data); }

  // Blogs
  getBlogs(page = 1, pageSize = 20, search = '', status = '', category = ''): Promise<ValidationResult<PagedResponse<Blog>>> {
    return this.api.get('/v1/blog', { params: { page, pageSize, search: search || undefined, status: status || undefined, category: category || undefined } }).then(r => r.data);
  }
  getBlog(id: number): Promise<ValidationResult<Blog>> {
    return this.api.get(`/v1/blog/${id}`).then(r => r.data);
  }
  createBlog(blog: Partial<Blog>): Promise<ValidationResult<Blog>> {
    return this.api.post('/v1/blog', blog).then(r => r.data);
  }
  updateBlog(id: number, blog: Partial<Blog>): Promise<ValidationResult<Blog>> {
    return this.api.put(`/v1/blog/${id}`, blog).then(r => r.data);
  }
  deleteBlog(id: number): Promise<ValidationResult<any>> {
    return this.api.delete(`/v1/blog/${id}`).then(r => r.data);
  }
  publishBlog(id: number): Promise<ValidationResult<any>> {
    return this.api.post(`/v1/blog/${id}/publish`).then(r => r.data);
  }
  updateBlogStatus(id: number, status: string): Promise<ValidationResult<any>> {
    return this.api.patch(`/v1/blog/${id}/status`, { status }).then(r => r.data);
  }

  // ── Admin Wallet ──────────────────────────────────────────────────────────

  getAdminWallet(technicianId: number): Promise<any> {
    return this.api.get(`/v1/admin/technician/${technicianId}/wallet`).then(r => r.data);
  }

  getAdminWalletTransactions(technicianId: number, page = 1, pageSize = 20): Promise<any[]> {
    return this.api.get(`/v1/admin/technician/${technicianId}/wallet/transactions`, {
      params: { page, pageSize },
    }).then(r => r.data);
  }

  getAdminBankAccount(technicianId: number): Promise<any> {
    return this.api.get(`/v1/admin/technician/${technicianId}/bank-account`).then(r => r.data);
  }

  saveAdminBankAccount(technicianId: number, payload: {
    accountHolderName: string;
    accountNumber: string;
    ifsc: string;
    bankName: string;
  }): Promise<any> {
    return this.api.post(`/v1/admin/technician/${technicianId}/bank-account`, payload).then(r => r.data);
  }

  adjustAdminWallet(technicianId: number, payload: {
    amount: number;
    type: 'CREDIT' | 'DEBIT';
    notes: string;
  }): Promise<{ message: string }> {
    return this.api.post(`/v1/admin/technician/${technicianId}/wallet/adjust`, payload).then(r => r.data);
  }

  // ── Plans ──────────────────────────────────────────────────────────────────

  async getPlans(): Promise<any[]> {
    const response = await this.api.get('/v1/plans');
    return response.data;
  }

  async updateActionItemStatus(id: number, status: string): Promise<void> {
    await this.api.patch(`/v1/plans/action-items/${id}/status`, { status });
  }

  // ── Telecaller ────────────────────────────────────────────────────────────
  async getTelecallers(): Promise<{ id: number; name: string }[]> {
    const response = await this.api.get('/v1/leads/telecallers');
    return response.data;
  }

  async assignLeadTelecaller(leadId: number, telecallerId: number, telecallerName: string): Promise<void> {
    await this.api.put(`/v1/leads/${leadId}/assign-telecaller`, { telecallerId, telecallerName });
  }

  async bulkAssignTelecaller(leadIds: number[], telecallerId: number, telecallerName: string): Promise<void> {
    await this.api.put('/v1/leads/bulk-assign-telecaller', { leadIds, telecallerId, telecallerName });
  }

  async updateActionItem(id: number, data: {
    actionItem?: string;
    category?: string;
    dueDate?: string;
    assignee?: string;
    priority?: string;
    status?: string;
    notes?: string;
  }): Promise<void> {
    await this.api.patch(`/v1/plans/action-items/${id}`, data);
  }

  // ── Coupons ───────────────────────────────────────────────────────────────
  getCoupons(): Promise<any> { return this.api.get('/v1/Coupon').then(r => r.data); }
  createCoupon(data: any): Promise<any> { return this.api.post('/v1/Coupon', data).then(r => r.data); }
  updateCoupon(id: number, data: any): Promise<any> { return this.api.put(`/v1/Coupon/${id}`, data).then(r => r.data); }
  deleteCoupon(id: number): Promise<any> { return this.api.delete(`/v1/Coupon/${id}`).then(r => r.data); }

  // Error handling helper
  handleError(error: any): string {
    if (error.response?.data?.errors?.length > 0) {
      return error.response.data.errors.join(', ');
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  }
}

export const apiService = new ApiService();
export default apiService; 
